import React, { useEffect, useState } from 'react';
import { Table, Form, Input, Button, Card, Row, Col, Space, message } from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import FilterPanel from '../../components/common/FilterPanel';
import { menuService } from '../../services/menuService';
import MenuCreateForm from './MenuCreateForm';
import './index.css';
import { useTranslation } from 'react-i18next';

// 递归过滤菜单树
const filterMenuTree = (tree, searchValues) => {
  if (!tree || tree.length === 0) return [];
  
  return tree.filter(item => {
    let match = true;
    
    // 根据搜索条件过滤
    if (searchValues.name && !item.name?.toLowerCase().includes(searchValues.name.toLowerCase())) {
      match = false;
    }
    if (searchValues.key && !item.key?.toLowerCase().includes(searchValues.key.toLowerCase())) {
      match = false;
    }
    
    // 递归过滤子节点
    if (item.children && Array.isArray(item.children)) {
      const filteredChildren = filterMenuTree(item.children, searchValues);
      item.children = filteredChildren;
      
      // 如果子节点有匹配的，父节点也要显示
      if (filteredChildren.length > 0) {
        match = true;
      }
    }
    
    return match;
  }).map(item => ({ ...item })); // 创建副本避免修改原数据
};

const MenuManagement = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [menuData, setMenuData] = useState([]);
  const { t } = useTranslation();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${t('common.operation')} ${range[0]}-${range[1]} / ${t('common.total')} ${total}`
  });
  const [createVisible, setCreateVisible] = useState(false);

  const columns = [
    { title: t('menuManagement.name'), dataIndex: 'name', key: 'name' },
    { title: t('menuManagement.key'), dataIndex: 'key', key: 'key' },
    { title: t('menuManagement.sort'), dataIndex: 'sort', key: 'sort' },
    { title: t('menuManagement.actions'), key: 'actions', render: (_, record) => <span>--</span> },
  ];

  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const searchParams = {
        appDomain: 'WMS',
        currentPage: params.currentPage || pagination.current,
        pageSize: params.pageSize || pagination.pageSize,
        ...form.getFieldsValue(),
      };
      const res = await menuService.getMenuTree(searchParams);
      if (res && res.success && res.data) {
        const root = res.data;
        const searchValues = form.getFieldsValue();
        const treeData = filterMenuTree(root.children || [], searchValues);
        setMenuData(treeData);
        setPagination(prev => ({
          ...prev,
          total: treeData.length,
          current: searchParams.currentPage,
          pageSize: searchParams.pageSize,
        }));
      } else {
        message.error(res?.message || '查询失败');
      }
    } catch (e) {
      message.error('查询失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  const handleSearch = () => {
    const formValues = form.getFieldsValue();
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchData({ currentPage: 1 });
  };

  const handleReset = () => {
    form.resetFields();
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchData({ currentPage: 1 });
  };

  const handleTableChange = (paginationConfig) => {
    setPagination(paginationConfig);
    fetchData({
      currentPage: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    });
  };

  const handleCreate = async (values) => {
    const data = {
      ...values,
      parentCode: '/root',
      resourceType: 'MENU',
      appDomain: 'WMS',
      pageType: '1'
    };
    const res = await menuService.createMenu(data);
    if (res && res.success) {
      message.success('创建成功');
      setCreateVisible(false);
      fetchData();
    } else {
      message.error(res?.message || '创建失败');
    }
  };

  return (
    <div>
      <FilterPanel
        onFilter={(values) => {
          // 更新表单值并触发搜索
          form.setFieldsValue(values);
          handleSearch();
        }}
        onReset={handleReset}
        initialValues={form.getFieldsValue()}
        className="menu-management-search"
        title={t('menuManagement.title')}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateVisible(true)}>
            {t('menuManagement.add')}
          </Button>
        }
      >
        <Col span={6}>
          <Form.Item name="name" label={t('menuManagement.name')}>
            <Input placeholder={t('menuManagement.name') + t('common.search')} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="key" label={t('menuManagement.key')}>
            <Input placeholder={t('menuManagement.key') + t('common.search')} />
          </Form.Item>
        </Col>
      </FilterPanel>

      <Card className="menu-management-table">
        <Table
          columns={columns}
          dataSource={menuData}
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          rowKey="id"
          childrenColumnName="children"
          defaultExpandAllRows={false}
          expandable={{
            defaultExpandedRowKeys: [],
            expandRowByClick: false,
          }}
          scroll={{ x: 1200, y: 500 }}
        />
      </Card>
      <MenuCreateForm visible={createVisible} onCancel={() => setCreateVisible(false)} onOk={handleCreate} />
    </div>
  );
};

export default MenuManagement;